import React, { useState, useEffect, useRef, useCallback } from 'react';
import useStore from '../../../../store';
import { useTranslation } from 'react-i18next';
import { FaShareAlt } from 'react-icons/fa';
import { useArcadeShare } from '../../ui/useArcadeShare';
import './Game2048.css';

const MOVE_THROTTLE_MS = 250; // Ограничение: 1 ход в 0.25 сек

const useAnimatedTiles = (serverState) => {
    const [visualTiles, setVisualTiles] = useState([]);

    // Храним таймер, чтобы очищать его при быстрых ходах
    const cleanupTimerRef = useRef(null);

    useEffect(() => {
        if (!serverState || !serverState.grid) return;

        const newGrid = serverState.grid;

        // Формируем новый список плиток для рендеринга
        const nextTiles = [];

        newGrid.forEach((cell, idx) => {
            if (!cell) return;

            // 1. Добавляем РЕАЛЬНУЮ плитку (победителя)
            // Она будет стоять на своей новой позиции (idx)
            nextTiles.push({
                ...cell,
                idx: idx,
                isGhost: false,
                // Важно: если плитка только что родилась (merge),
                // мы хотим, чтобы она "попнула" (pop animation) только после приезда призраков.
                // Но CSS handled it via .merged class.
            });

            // 2. ОБРАБОТКА СЛИЯНИЙ (Создание призраков)
            // Если сервер сказал, что эта плитка (cell) поглотила кого-то (mergedIds)
            if (cell.mergedIds && cell.mergedIds.length > 0) {
                cell.mergedIds.forEach(victimId => {
                    // Мы создаем "фэйковую" плитку.
                    // КЛЮЧЕВОЙ МОМЕНТ:
                    // ID у неё = ID жертвы (victimId).
                    // Позиция (idx) = Позиция ПОБЕДИТЕЛЯ (idx).
                    // Значение (val) = Половина от победителя (то есть старое значение).

                    nextTiles.push({
                        id: victimId,       // React найдет старый DOM-элемент по этому ID
                        val: cell.val / 2,  // Рисуем её со старой цифрой
                        idx: idx,           // Отправляем её в ту же ячейку, где стоит победитель
                        isGhost: true,      // Помечаем как призрака
                        key: victimId       // Это заставит React анимировать движение
                    });
                });
            }
        });

        // Обновляем стейт, чтобы React отрисовал движение
        setVisualTiles(nextTiles);

        // 3. ОЧИСТКА ПРИЗРАКОВ
        // Через 200мс (время CSS transition) мы удаляем призраков,
        // оставляя только реальные плитки.
        if (cleanupTimerRef.current) clearTimeout(cleanupTimerRef.current);

        cleanupTimerRef.current = setTimeout(() => {
            setVisualTiles(prev => prev.filter(t => !t.isGhost));
        }, 200); // Синхронизировать с CSS transition speed

        return () => clearTimeout(cleanupTimerRef.current);

    }, [serverState]); // Срабатывает каждый раз, когда приходит апдейт от сервера

    return visualTiles;
};

const Game2048 = ({ socket, onClose, timeLeft }) => {
  const { t } = useTranslation();
  const store = useStore.getState();

  const [state, setState] = useState(null);
  const [finalData, setFinalData] = useState(null);

  // Добавляем локальное состояние видимости отчета
  const [showResult, setShowResult] = useState(false);

  // Ref для ограничения частоты ходов
  const lastMoveTimeRef = useRef(0);

  // === ДОБАВЛЯЕМ ХУК ШЕРИНГА ===
  const { boardRef, share, isSharing, isCapturing } = useArcadeShare(socket);
  // =============================

  // Инициализация
  useEffect(() => {
    if (!socket) return;
    store.ref.work.methods.hostAction('mafiaMiniGameAction', { type: 'init' });

    const handleEvent = (data) => {
      // 1. Обычное обновление (или игнор хода)
      if (data.type === 'update' || data.type === 'score_update' || data.type === 'ignore') {
        if (data.state) setState(data.state);
      }
      // 2. Конец игры (заполнилось поле)
      else if (data.type === 'finish') {
        if (data.state) setState(data.state);
        setTimeout(() => {
            setFinalData(data);
            setShowResult(true); // <--- Показываем результат
            window.croco?.haptic?.('error');
        }, 1000); // Сокращаем задержку до 1 сек
      }
      // 3. Конец игры (вышло время - приходит от arcadeLogic)
      else if (data.type === 'game_over') {
         // Тут нет state, так как игра остановлена извне
         setFinalData(data);
         setShowResult(true); // <--- Показываем результат
         window.croco?.haptic?.('error');
      }
    };

    socket.on('arcade:event', handleEvent);
    return () => socket.off('arcade:event', handleEvent);
  }, [socket, store]);

  const sendMove = useCallback((direction) => {
    // ИЗМЕНЕНИЕ: Разрешаем ход, если данные есть, но окно закрыто (!showResult)
    if (!state) return;
    if (showResult) return; // Блокируем только если окно открыто

    const now = Date.now();
    if (now - lastMoveTimeRef.current < MOVE_THROTTLE_MS) return;

    lastMoveTimeRef.current = now;
    store.ref.work.methods.hostAction('mafiaMiniGameAction', { type: 'move', direction });
  }, [state, showResult, store.ref.work.methods]); // Зависимость от showResult

  // Клавиатура
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(e.key)) {
        e.preventDefault(); // Блокируем скролл страницы стрелками
        const dir = e.key.replace('Arrow', '').toLowerCase();
        sendMove(dir);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [sendMove]);

  // Свайпы
  const touchStartRef = useRef(null);
  const handleTouchStart = (e) => {
    touchStartRef.current = { x: e.touches[0].clientX, y: e.touches[0].clientY };
  };
  const handleTouchEnd = (e) => {
    if (!touchStartRef.current) return;
    const dx = e.changedTouches[0].clientX - touchStartRef.current.x;
    const dy = e.changedTouches[0].clientY - touchStartRef.current.y;

    if (Math.abs(dx) > Math.abs(dy)) {
      if (Math.abs(dx) > 30) sendMove(dx > 0 ? 'right' : 'left');
    } else {
      if (Math.abs(dy) > 30) sendMove(dy > 0 ? 'down' : 'up');
    }
    touchStartRef.current = null;
  };

  // ПОДКЛЮЧАЕМ НАШ ХУК ВМЕСТО ОБЫЧНОГО МАППИНГА
  const tilesToRender = useAnimatedTiles(state);

  if (!state) return null;

  // --- ПОДГОТОВКА ДАННЫХ ДЛЯ РЕЗУЛЬТАТА ---
  let myStats = {};
  if (finalData) {
      // Данные могут прийти в двух форматах:
      // 1. { type: 'finish', finalData: { my: { ... } } } - от самой игры
      // 2. { type: 'game_over', my: { ... } } - от arcadeLogic (таймаут)
      myStats = finalData.my || finalData.finalData?.my || {};
  }

  const size = state.gridSize;

  const getTileStyle = (index) => {
    const row = Math.floor(index / size);
    const col = index % size;
    const percentage = 100 / size;

    return {
        width: `${percentage}%`,
        height: `${percentage}%`,
        // Используем transform для аппаратного ускорения
        transform: `translate(${col * 100}%, ${row * 100}%)`,
        position: 'absolute',
        left: 0,
        top: 0
    };
  };

  return (
    <div
      className="g2048-board"
      ref={boardRef} // <--- ПРИВЯЗЫВАЕМ СЮДА
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
    >

      {/* Оверлей результатов */}
      {/* ДОБАВЛЯЕМ && !isCapturing, чтобы скрыть меню во время скрина */}
      {finalData && showResult && !isCapturing && (
        <div className="g2048-result-overlay">
            <div className="g2048-result-card">

                <div className="g2048-game-over-text">
                  {t('arcade.twoZeroFourEight_game_over')}
                </div>

                <div className="g2048-rank">
                    {t(`arcade.${myStats.rank || 'twoZeroFourEight_rank_script_kiddie'}`)}
                </div>

                {/* Панель статистики */}
                <div className="g2048-stats-panel">
                  <div className="g2048-stat-item">
                    <span className="g2048-stat-label">{t('arcade.twoZeroFourEight_score')}</span>
                    <span className="g2048-stat-value">{myStats.score || state.score}</span>
                  </div>

                  <div className="g2048-stat-item">
                    <span className="g2048-stat-label">{t('arcade.twoZeroFourEight_max_tile')}</span>
                    <span className="g2048-stat-value">
                      {myStats.maxTile || myStats.bestTile || state.bestTile}
                    </span>
                  </div>
                </div>

                <div style={{display: 'flex', flexDirection: 'column', gap: '10px'}}>

                    <button
                        className="g2048-btn"
                        style={{ background: '#3b82f6', color: '#fff', boxShadow: '0 4px 0 #2563eb' }}
                        onClick={() => share(myStats, { backgroundColor: '#1e1e2e' })}
                        disabled={isSharing}
                    >
                        <FaShareAlt style={{marginRight: '8px'}} />
                        {isSharing ? t('arcade.share_preparing') : t('arcade.share')}
                    </button>

                    <button className="g2048-btn" onClick={onClose}>
                        {t('arcade.close')}
                    </button>

                    <button
                        className="g2048-btn"
                        style={{ background: 'transparent', border: '2px solid #555', color: '#ccc', boxShadow: 'none' }}
                        onClick={() => setShowResult(false)}
                    >
                        {t('arcade.stay')}
                    </button>
                </div>
            </div>
        </div>
      )}

      {/* Шапка */}
      <div className="g2048-header">
        <div className="g2048-score-box">
             <div className="g2048-label">{t('arcade.twoZeroFourEight_score')}</div>
             <div className="g2048-value">{state.score}</div>
        </div>
        <div className="g2048-score-box">
             <div className="g2048-label">{t('arcade.twoZeroFourEight_max_tile')}</div>
             <div className="g2048-value">{state.bestTile}</div>
        </div>
        {/* Таймер теперь без инлайн стилей, цвет задан в CSS */}
        <div className="g2048-score-box timer-box">
             <div className="g2048-label">{t('arcade.twoZeroFourEight_timer')}</div>
             <div className="g2048-value">
                 {Math.floor(timeLeft / 60)}:{(timeLeft % 60).toString().padStart(2, '0')}
             </div>
        </div>
      </div>

      {/* Игровое поле */}
      <div className="g2048-grid-container">

          {/* Слой фона (пустые ячейки) */}
          <div className="g2048-grid-background" style={{
              gridTemplateColumns: `repeat(${size}, 1fr)`,
              gridTemplateRows: `repeat(${size}, 1fr)`
          }}>
              {Array(size * size).fill(0).map((_, i) => (
                  <div key={i} className="g2048-cell-placeholder">
                      <div className="g2048-cell-inner"></div>
                  </div>
              ))}
          </div>

          {/* СЛОЙ ПЛИТОК */}
          <div className="g2048-tile-layer">
              {tilesToRender.map((tile) => {
                  // Определяем классы
                  const isSuper = tile.val > 2048;
                  const tileClass = `tile-${isSuper ? 'super' : tile.val}`;

                  // Анимация "pop" (увеличение) для слияния
                  const mergedClass = (tile.merged && !tile.isGhost) ? ' merged' : '';

                  // ДОБАВЛЕНО: Анимация "appear" (появление) для новой плитки
                  // Применяем только если это не призрак и флаг isNew пришел с сервера
                  const newClass = (tile.isNew && !tile.isGhost) ? ' new' : '';

                  // ДОБАВЛЕНО: Расчет количества цифр для CSS
                  const valStr = tile.val.toString();
                  const digitCount = valStr.length;

                  // Z-Index важен!
                  // Призрак должен быть ПОД реальной плиткой (zIndex 5)
                  // Реальная плитка СВЕРХУ (zIndex 10)
                  const zStyle = { zIndex: tile.isGhost ? 5 : 10 };

                  return (
                      <div
                          // КЛЮЧЕВОЕ: key должен быть ID плитки.
                          // Для призрака это ID удаленной плитки -> React видит перемещение.
                          key={tile.id}
                          className="g2048-tile-wrapper"
                          style={{
                              ...getTileStyle(tile.idx),
                              ...zStyle
                          }}
                      >
                          {/* ИЗМЕНЕНИЕ: Добавляем newClass и data-digits в список классов */}
                          <div
                              className={`g2048-tile-inner ${tileClass}${mergedClass}${newClass}`}
                              data-digits={digitCount}
                          >
                              {tile.val}
                          </div>
                      </div>
                  );
              })}
          </div>
      </div>
    </div>
  );
};

export default Game2048;